import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API_BASE = `${BACKEND_URL}/api`;

/**
 * AUTENTICAZIONE: SOLO COOKIE.
 *
 * Fino al 25 agosto 2026 il token stava anche in localStorage e viaggiava in
 * query string. Erano due copie leggibili da JavaScript: un XSS ne portava via
 * una valida 24 ore, quella dell'admin compresa, e il secondo fattore non
 * serviva a niente perche' agisce al momento del login, non dopo.
 *
 * Ora la sessione vive nei cookie `httpOnly`, che JavaScript non puo' leggere.
 * Un XSS puo' ancora agire come l'utente finche' la pagina e' aperta, ma non
 * puo' portarsi via niente da usare altrove e con calma.
 *
 * `withCredentials: true` fa mandare i cookie ad axios. Per immagini, PDF e
 * download non serve nulla: il browser li manda da solo, stesso dominio.
 */

/** URL di un file protetto, usabile come `src` di <img>, <iframe>, <embed>.
 *  Nessun token: ci pensa il cookie di sessione. */
export const authedFileUrl = (fileId) => {
  if (!fileId) return null;
  return `${API_BASE}/files/${fileId}`;
};

/** Risolve il logo dello studio (assoluto o relativo al backend) in un `src`. */
export const resolveLogoUrl = (logoUrl) => {
  if (!logoUrl) return null;
  return logoUrl.startsWith("http") ? logoUrl : `${BACKEND_URL}${logoUrl}`;
};

/** Scarica una risorsa protetta e forza il salvataggio del file.
 *  `credentials: "include"` al posto dell'intestazione Bearer: la sessione
 *  e' nel cookie. Solleva se la risposta non e' 2xx. */
export async function downloadAuthed(url, filename) {
  const res = await fetch(url, { credentials: "include" });
  if (!res.ok) throw new Error(`Download failed (${res.status})`);
  const blob = await res.blob();
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(a.href);
}

export const api = axios.create({
  baseURL: API_BASE,
  withCredentials: true,
  headers: { "Content-Type": "application/json" },
});

/* ── Rinnovo della sessione ─────────────────────────────────────────────
 *
 * L'access token dura 2 ore, il refresh 7 giorni e si rinnova a ogni uso:
 * chi lavora non viene mai interrotto, chi sparisce una settimana rifa' il
 * login. Prima l'access durava 24 ore DAL LOGIN e la planner veniva sbattuta
 * fuori a meta' giornata.
 */

/** Evento emesso quando il rinnovo fallisce: la sessione e' finita davvero.
 *  `lib/api.js` non conosce le rotte e non fa redirect, ci pensa AuthContext. */
export const SESSIONE_SCADUTA = "aurea:sessione-scaduta";
export const ABBONAMENTO_RICHIESTO = "aurea:abbonamento-richiesto";

/** Chiamate in cui un 401 significa "credenziali sbagliate", non "sessione
 *  scaduta": rinnovare non avrebbe senso. `/auth/me` NON e' in questa lista di
 *  proposito, ed e' il caso piu' importante: e' la chiamata che parte al
 *  ricaricamento della pagina. */
const SENZA_RINNOVO = [
  "/auth/refresh", "/auth/login", "/auth/admin-login",
  "/auth/admin-login/2fa", "/auth/register",
];

/** Minuti dopo i quali vale la pena rinnovare al ritorno sulla scheda. */
const MINUTI_PRIMA_DI_RINNOVARE = 60;

let rinnovoInCorso = null;          // una sola chiamata alla volta
let ultimoRinnovo = Date.now();     // l'app si apre con i cookie appena usati
let impersonazioneInCorso = false;

/** Durante un'impersonazione il cookie di refresh e' quello dell'ADMIN e il
 *  backend risponde 401 di proposito. Chiamarlo lo stesso ucciderebbe ogni
 *  impersonazione al primo cambio di finestra. Lo imposta AuthContext. */
export const impostaImpersonazione = (attiva) => {
  impersonazioneInCorso = Boolean(attiva);
};

/** Come puo' finire un tentativo di rinnovo. La distinzione fra SCADUTA e
 *  INCERTA e' il punto: solo la prima e' un logout. */
export const ESITO = {
  RINNOVATO: "rinnovato",        // il server ha ruotato: coppia nuova nei cookie
  GIA_RINNOVATO: "gia-rinnovato", // un'altra scheda e' arrivata prima: va bene uguale
  SCADUTA: "scaduta",             // il server ha detto di no: la sessione e' finita
  INCERTA: "incerta",             // non si sa: 5xx, timeout, rete. NON e' un logout
};

/** Quante volte ritentare un rinnovo caduto per motivi di trasporto. */
const RITENTATIVI_TRASPORTO = 2;

const attendi = (ms) => new Promise((r) => setTimeout(r, ms));

async function _chiedeRinnovo(tentativo = 0) {
  try {
    const { data } = await api.post("/auth/refresh");
    ultimoRinnovo = Date.now();
    return data?.rinnovato ? ESITO.RINNOVATO : ESITO.GIA_RINNOVATO;
  } catch (errore) {
    const stato = errore?.response?.status;
    // Il server ha risposto e ha detto di no: questa e' una risposta, non un
    // guasto. La sessione e' finita davvero.
    if (stato === 401 || stato === 403) return ESITO.SCADUTA;
    // Tutto il resto (5xx, timeout, rete caduta) non dice NIENTE sullo stato
    // della sessione. `fetchMe` in AuthContext ha gia' questa regola scritta:
    // un 5xx non deve sloggare nessuno. Il rinnovo la seguiva al contrario, e
    // un 502 nel momento in cui scade l'access token, cioe' ogni due ore,
    // buttava fuori la planner. Si ritenta un numero limitato di volte.
    if (tentativo < RITENTATIVI_TRASPORTO) {
      await attendi(600 + tentativo * 600);
      return _chiedeRinnovo(tentativo + 1);
    }
    // Esauriti i ritentativi resta l'incertezza, e l'incertezza si dichiara:
    // non si inventa uno stato di autenticazione, ne' valido ne' morto. Sara'
    // una richiesta successiva a scioglierla.
    return ESITO.INCERTA;
  }
}

/** Rinnova la sessione. Se un rinnovo e' gia' in volo, aspetta quello: dieci
 *  401 insieme non devono diventare dieci rinnovi.
 *  Torna uno dei valori di `ESITO`. */
export function rinnovaSessione() {
  // QUESTA e' la guardia che regge davvero l'impersonazione, non quella
  // nell'interceptor: il rinnovo al ritorno sulla scheda (`rinnovaSeVecchio`,
  // chiamato da AuthContext su `visibilitychange`) non passa dall'interceptor
  // e arriva qui direttamente. Togliendola, ogni impersonazione morirebbe al
  // primo cambio di finestra.
  if (impersonazioneInCorso) return Promise.resolve(ESITO.SCADUTA);
  if (!rinnovoInCorso) {
    const chiamata = _chiedeRinnovo();
    rinnovoInCorso = chiamata;
    chiamata.finally(() => { rinnovoInCorso = null; });
  }
  return rinnovoInCorso;
}

/** Rinnovo al ritorno sulla scheda, ma solo se l'ultimo e' vecchio di un'ora:
 *  senza la soglia partirebbe a ogni alt-tab. Copre immagini, PDF e download,
 *  che partono da <img src> e da fetch e non passano dall'interceptor. */
export function rinnovaSeVecchio() {
  if (Date.now() - ultimoRinnovo < MINUTI_PRIMA_DI_RINNOVARE * 60_000) {
    return Promise.resolve(ESITO.GIA_RINNOVATO);
  }
  return rinnovaSessione();
}

api.interceptors.response.use(
  (risposta) => risposta,
  async (errore) => {
    if (errore?.response?.status === 402 && errore.response.data?.detail?.code === "workspace_subscription_required") {
      window.dispatchEvent(new Event(ABBONAMENTO_RICHIESTO));
    }
    const richiesta = errore?.config;
    if (errore?.response?.status !== 401 || !richiesta) return Promise.reject(errore);

    // Gia' rinnovato e gia' ripetuto: la sessione e' finita davvero, per
    // esempio per un taglio sessions_valid_from. Nessun secondo rinnovo: e'
    // questo vincolo a rendere impossibile un ciclo.
    if (richiesta._ritentata) {
      window.dispatchEvent(new Event(SESSIONE_SCADUTA));
      return Promise.reject(errore);
    }

    const percorso = richiesta.url || "";
    if (SENZA_RINNOVO.some((p) => percorso.startsWith(p))) return Promise.reject(errore);

    // Scorciatoia, non protezione: togliendola il comportamento osservabile non
    // cambia, perche' `rinnovaSessione` ha la sua guardia e tornerebbe subito
    // `ok: false`, portando allo stesso evento e allo stesso rifiuto. Sta qui
    // per dire a chi legge che questo caso e' previsto. La guardia che conta e'
    // quella dentro `rinnovaSessione`: non cancellarla pensando che basti
    // questa, perche' il rinnovo al ritorno sulla scheda non passa di qui.
    if (impersonazioneInCorso) {
      window.dispatchEvent(new Event(SESSIONE_SCADUTA));
      return Promise.reject(errore);
    }

    const esito = await rinnovaSessione();

    if (esito === ESITO.INCERTA) {
      // Non si sa se la sessione sia ancora buona: NON si slogga. Il 401
      // originale verrebbe pero' letto da `fetchMe` come "sloggato", quindi
      // lo si marca perche' venga trattato come guasto passeggero.
      errore.rinnovoIncerto = true;
      return Promise.reject(errore);
    }

    if (esito === ESITO.SCADUTA) {
      window.dispatchEvent(new Event(SESSIONE_SCADUTA));
      return Promise.reject(errore);
    }

    // Un solo ritentativo, sia dopo una rotazione riuscita sia dopo un
    // "ha gia' rinnovato un'altra scheda": in entrambi i casi la coppia buona
    // e' nei cookie.
    richiesta._ritentata = true;
    return api(richiesta);
  }
);

/** Spia di sessione: cookie NON httpOnly che vale "1" quando una sessione
 *  esiste. Non contiene il token, dice soltanto se ha senso chiamare
 *  /auth/me. Serve a non sparare un 401 garantito sulle pagine pubbliche. */
export const hasSessionHint = () =>
  document.cookie.split(";").some((c) => c.trim().startsWith("aurea_sessione="));

export function formatApiError(detail, fallback = "Errore imprevisto. Riprova.") {
  if (detail == null) return fallback;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}
